import { msg } from "@lingui/core/macro";
/**
 * PLUGIN-KERNEL — mounts the app's composition and re-mounts it whenever the
 * active account changes.
 */
import {
  type Component,
  createEffect,
  createMemo,
  onCleanup,
  type ParentComponent,
} from "solid-js";
import { Kernel, KernelContext, useKernel } from "@fleet/kernel";
import { showToast } from "@/components/ui/toast";
import { useShortcuts } from "@fleet/shortcuts";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { isTauriRuntime } from "@/lib/platform";
import { appComposition, type AppContribution, type HostServices } from "@/composition";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";

const KernelShortcutsBridge: Component = () => {
  const app = useKernel<AppContribution>();
  const shortcuts = useShortcuts();

  createEffect(() => {
    const disposers = app().contributions.flatMap((c) => {
      if (c.kind !== "command") {
        return [];
      }
      const out = [shortcuts.registry.registerCommand(c.command)];
      if (c.binding) {
        out.push(shortcuts.registry.registerBinding(c.binding));
      }
      return out;
    });
    onCleanup(() => {
      for (const dispose of disposers) {
        dispose();
      }
    });
  });

  return null;
};

export const KernelProvider: ParentComponent = (props) => {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();

  const host: HostServices = {
    db,
    drizzle,
    userId: () => session.userId() || undefined,
    onHistoryError: () => showToast(t(msg`Could not load older activity`), "error"),
    translate: t,
  };

  const target = isTauriRuntime() ? "tauri" : "web";
  const kernel = new Kernel<AppContribution>();

  const mounted = createMemo(() => {
    const accountId = session.userId() || null;
    const app = kernel.mount(appComposition(host, target), { accountId });
    onCleanup(() => app.unmount());
    return app;
  });

  return (
    <KernelContext.Provider value={mounted}>
      <KernelShortcutsBridge />
      {props.children}
    </KernelContext.Provider>
  );
};
